/**
 * VideoPlayerModal
 * Headless UI dialog that embeds a YouTube/Vimeo video via iframe.
 */

import { Fragment } from 'react';
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';
import { XMarkIcon, PlayCircleIcon } from '@heroicons/react/24/outline';

/**
 * Convert a video page URL into an embeddable iframe URL.
 * Supports Vimeo and YouTube links. Returns the original URL if already embeddable or unrecognised.
 */
const toEmbedUrl = (url) => {
  if (!url) return url;

  // Vimeo: https://vimeo.com/226053498 → https://player.vimeo.com/video/226053498
  const vimeoMatch = url.match(/(?:www\.)?vimeo\.com\/(\d+)/);
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;

  // YouTube: various formats → https://www.youtube.com/embed/{id}
  const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]+)/);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;

  return url;
};

/**
 * VideoPlayerModal component
 * @param {Object} props
 * @param {Object|null} props.resource - Resource to display (null = closed)
 * @param {Function} props.onClose - Close handler
 */
const VideoPlayerModal = ({ resource, onClose }) => {
  const isOpen = !!resource;

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        {/* Backdrop */}
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/70" />
        </TransitionChild>

        {/* Modal panel */}
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className="w-full max-w-3xl bg-white dark:bg-gray-900 rounded-2xl shadow-2xl overflow-hidden">
                {/* Orange accent bar */}
                <div className="h-1 bg-gradient-to-r from-orange-400 via-orange-500 to-orange-400" />

                {/* Header */}
                <div className="flex items-start justify-between p-4 sm:p-5 border-b border-gray-100 dark:border-gray-800">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-xl bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center flex-shrink-0">
                      <PlayCircleIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div className="min-w-0">
                      <DialogTitle className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white leading-snug truncate">
                        {resource?.title}
                      </DialogTitle>
                      {resource?.categoryName && (
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                          {resource.categoryName}
                        </p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={onClose}
                    className="ml-3 p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors flex-shrink-0"
                    aria-label="Close video"
                  >
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                </div>

                {/* Video embed */}
                <div className="relative w-full aspect-video bg-black">
                  {resource?.videoUrl ? (
                    <iframe
                      key={resource.resourceId}
                      src={toEmbedUrl(resource.videoUrl)}
                      title={resource.title}
                      className="absolute inset-0 w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-gray-500 dark:text-gray-400 text-sm">
                      No video URL provided
                    </div>
                  )}
                </div>

                {/* Description */}
                {resource?.description && (
                  <div className="px-4 sm:px-5 py-4 border-t border-gray-100 dark:border-gray-800">
                    <p className="text-sm text-gray-600 dark:text-gray-400">{resource.description}</p>
                  </div>
                )}
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default VideoPlayerModal;
